import { useAtom } from 'jotai';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { useQueryClient } from '@tanstack/react-query';
import { deleteReportConfirmDialogAtom } from '../atoms';
import { useOnClickOutside } from '../hooks';
import { useDeleteReportMutation } from '../lib/queries';
import { QUERY_KEYS, TOAST_DURATION } from '../lib/constants';

export function DeleteReportConfirmDialog() {
  const { t } = useTranslation();
  const [state, setState] = useAtom(deleteReportConfirmDialogAtom);
  const dialog = useRef<HTMLDialogElement | null>(null);
  const deleteReportMutation = useDeleteReportMutation();
  const queryClient = useQueryClient();

  const handleClose = () => {
    setState({ isOpen: false, report: undefined });
    dialog.current?.close();
  };

  const handleSubmit = () => {
    if (state.report)
      deleteReportMutation.mutate(state.report.id, {
        onSuccess: () => {
          toast.success(t('report.deleted', { name: state.report?.name }), {
            duration: TOAST_DURATION,
          });
          handleClose();
          queryClient.refetchQueries({ queryKey: [QUERY_KEYS.REPORTS] });
        },
      });
  };

  useOnClickOutside(dialog.current, handleClose);

  useEffect(() => {
    if (!dialog.current?.open && state.isOpen) dialog.current?.showModal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.isOpen]);

  return (
    <dialog ref={dialog} className="max-w-xl">
      <span className="whitespace-normal text-title">
        {t('report.deleteDialog.description', { name: state.report?.name })}
      </span>

      <div className="flex justify-end gap-2">
        <button className="primary-fill" onClick={handleSubmit}>
          {t('common.yes')}
        </button>
        <button className="primary-outline" onClick={handleClose}>
          {t('common.cancel')}
        </button>
      </div>
    </dialog>
  );
}
